import {
  Component, Input, ViewEncapsulation, Output, EventEmitter, OnInit, ChangeDetectionStrategy, DestroyRef, inject
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith, switchMap} from 'rxjs/operators';

import {ProductService} from '../../../../../services/product.service';
import {AllocationGroup} from '../../../../../interfaces/allocation-group';

// This  component  should be user  only  for BW  products with  enabled allocated inventory

@Component({
  selector: 'app-order-allocation-group-control',
  templateUrl: './order-settings-allocation-group-control.component.html',
  styleUrls: ['./order-settings-allocation-group-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class OrderSettingsAllocationGroupControlComponent implements  OnInit {

  @Input() selectedAllocationGroupId: number;

  // a group backs one address only, so the ones already taken by the other rows are kept out of the list
  @Input() set excludedAllocationGroupIds(ids: number[]) {
    this.excludedAllocationGroupIds$.next(ids || []);
  }

  @Output() allocationGroupChanged = new EventEmitter<number>();

  private readonly excludedAllocationGroupIds$ = new BehaviorSubject<number[]>([]);

  searchSubject: Subject<string> = new Subject<string>();
  filteredAllocationGroups$: Observable<AllocationGroup[]>;
  allocationGroups$: Observable<AllocationGroup[]>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(private productService: ProductService) {
  }

  ngOnInit() {
    // the groups are shared and cached by the service, so no caching is needed here
    this.allocationGroups$ = this.productService.fetchAllocationGroups();

    const searchTerm$ = this.searchSubject.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      startWith(''),
    );

    // search by label in dropdown, over what the other rows have left available
    this.filteredAllocationGroups$ = combineLatest([searchTerm$, this.excludedAllocationGroupIds$])
      .pipe(
        switchMap(([term, excludedIds]) => this.allocationGroups$.pipe(
          map(groups => groups.filter(g => !excludedIds.includes(g.allocation_group_id))),
          map(groups => term
            ? groups.filter(g => g.allocation_group_label.toLowerCase().includes(term.toLowerCase()))
            : groups),
        )),
        // last in the pipe on purpose: it tears the whole chain down on destroy, including a request
        // still in flight inside the switchMap - takeWhile only gated the next source emission
        takeUntilDestroyed(this.destroyRef),
      );
  }

  protected onSelect(group: AllocationGroup) {
    if (!group) {
      return;
    }
    this.allocationGroupChanged.emit(group.allocation_group_id);
  }
}
